// The `/auth/*` route group — the Cloudflare edge handlers that own the
// sign-in hop, the `?code=` exchange, the httpOnly session cookie, and JWT
// verification for a static SPA. Four same-origin routes; everything else
// under `/auth/*` is 404. This module is NOT a Worker entry: the app's
// `index.ts` owns `main` and hands `handleAuth` every `/auth/*` path.
//
// The worker holds NO secrets and NO state: the raw broker JWT lives in the
// cookie, verified against the public JWKS. It only ever redirects or returns
// JSON — the SPA owns all UI — with a single deliberate exception: the
// circuit-breaker diagnostic page (guardrail 7), which must break a redirect
// loop that a `?auth_error=` redirect would otherwise perpetuate.

import {
  isSameOriginRequest,
  isTopLevelNavigation as isTopLevelNavigationHeaders,
} from "@bindery/shared/auth/fetchMetadata";
import { ReaderIdSchema } from "@bindery/shared/auth/readerId";
import { bytesToBase64Url } from "@bindery/shared/crypto";
import {
  INCLUDE_READERS_PARAM,
  READER_ID_PARAM,
} from "@bindery/shared/contracts/session";
import {
  AuthConfigError,
  DEFAULT_BASE_PATH,
  MAX_TRIES,
  STATE_BYTES,
  defaultOriginFor,
  exchangeAudienceFor,
  isAuthHostFor,
  resolveConfigFor,
  type AuthConfig,
  type AnyConsumerAppConfig,
  type AuthEnv,
} from "./config";
import { json as jsonResponse } from "@bindery/shared/http";
import type { CookieKit } from "./cookies";
import { exchangeCode } from "./exchange";
import type { AuthLogger } from "./log";
import type { ReturnToGuard } from "./return-to";
import { getRemoteJwks, verifyBrokerJwt } from "./verify";

export interface AuthRouteDeps {
  readonly app: AnyConsumerAppConfig;
  readonly cookies: CookieKit;
  readonly returnTo: ReturnToGuard;
  readonly log: AuthLogger;
  /** The checked route prefix, from `basePathOf(app)`. */
  readonly basePath: string;
  /** The checked fallback path, from `homePathOf(app)`. */
  readonly homePath: string;
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * A fresh sign-in nonce: `STATE_BYTES` CSPRNG bytes as base64url. base64url
 * rather than base64 because the broker rebuilds the callback's query through
 * `URLSearchParams`, which would turn a literal `+` into a space.
 */
function mintState(): string {
  const bytes = new Uint8Array(STATE_BYTES);
  crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
}

function redirect(location: string, cookies: string[] = []): Response {
  const headers = new Headers({ Location: location });
  for (const cookie of cookies) headers.append("Set-Cookie", cookie);
  return new Response(null, { status: 302, headers });
}

function json(
  status: number,
  body: unknown,
  options: { noStore?: boolean; cookies?: string[]; allow?: string } = {},
): Response {
  const headers = new Headers();
  // No CORS headers ever — cross-origin JS must not read these responses.
  if (options.allow !== undefined) headers.set("Allow", options.allow);
  for (const cookie of options.cookies ?? []) {
    headers.append("Set-Cookie", cookie);
  }
  return jsonResponse(status, body, {
    noStore: options.noStore ?? false,
    headers,
  });
}

/**
 * `405 Allow: GET` for the three `/auth/*` routes that are navigations. RFC
 * 9110 §15.5.6 requires the `Allow` header on a 405 — without it the status
 * says a method was wrong but not which one is right. These answered a flat
 * `401 method_not_allowed`, which claims the request was unauthenticated: a
 * client retrying with a token would be told the same thing again.
 */
function methodNotAllowed(): Response {
  return json(405, { error: "method_not_allowed" }, { allow: "GET" });
}

/**
 * Fetch-metadata gate for the two routes that SET a cookie — `/auth/sign-in`
 * and `/auth/callback` (on the callback, defence in depth behind the `state`
 * check). The exploit this closes is a cross-site SUBRESOURCE: `SameSite=Lax`
 * governs when a cookie is SENT, not when one may be SET, so an attacker's
 * `<img src="…/auth/callback?code=…">` would otherwise land a `Set-Cookie` and
 * silently sign the victim in as the attacker, and an `<img
 * src="…/auth/sign-in">` would overwrite the nonce of a sign-in in flight in
 * another tab. Such a request carries `Sec-Fetch-Dest: image` /
 * `Sec-Fetch-Mode: no-cors`; a real sign-in hop or return is always
 * `document` / `navigate`.
 *
 * Note this is NOT `handleSignOut`'s `sec-fetch-site` check, and must not be:
 * a legitimate callback arrives via a redirect from the broker, so the site
 * value depends on whether the serving host is same-site with it. A production
 * `*.sara.ai` consumer is same-site with `auth.sara.ai`; a `*.read-by-ear.com`
 * or `*.fugl.dev` one is not. Rejecting `cross-site` here would break those
 * hosts' sign-in outright.
 *
 * Absent headers are allowed: browsers too old to send fetch metadata still get
 * a working sign-in, and they are still covered by the `state` check, which is
 * the actual gate.
 *
 * The rule itself is `@bindery/shared/auth/fetchMetadata`'s, the one source the
 * broker's `/claim-reader` gate imports too, so the two cannot drift.
 */
function isTopLevelNavigation(request: Request): boolean {
  return isTopLevelNavigationHeaders(request.headers);
}

const SIGN_IN_ROUTE = "/sign-in";
const LIGHT_SIGN_IN_ROUTE = "/sign-in-light";
const MANAGE_ROUTE = "/manage";
const CALLBACK_ROUTE = "/callback";
const TOKEN_ROUTE = "/token";
const SIGN_OUT_ROUTE = "/sign-out";

export const SIGN_IN_PATH = `${DEFAULT_BASE_PATH}${SIGN_IN_ROUTE}`;
export const LIGHT_SIGN_IN_PATH = `${DEFAULT_BASE_PATH}${LIGHT_SIGN_IN_ROUTE}`;
export const MANAGE_PATH = `${DEFAULT_BASE_PATH}${MANAGE_ROUTE}`;

const LIGHT_SELECT_PARAM = "select";
const FLAG_ON = "true";

const MAX_FORM_BYTES = 4096;
const FORM_CONTENT_TYPE = "application/x-www-form-urlencoded";

/**
 * What a hop does with a `readerId`. `drop` forwards a proven, valid one and
 * otherwise carries on without it, because a sign-in with no reader is still
 * a valid outcome. `require` refuses instead: the light lane mints a fresh
 * reader when none arrives, which would orphan the device's progress.
 */
type ReaderIdPolicy = "ignore" | "drop" | "require";

interface BrokerHop {
  readonly event: `auth.${Lowercase<string>}`;
  readonly brokerPath: string;
  readonly readerId: ReaderIdPolicy;
  /** A flag forwarded only as the literal `true`; nothing else leaks. */
  readonly flag?: string;
}

const SIGN_IN_HOP: BrokerHop = {
  event: "auth.sign_in",
  brokerPath: "/redirect",
  readerId: "ignore",
};

const READER_SIGN_IN_HOP: BrokerHop = {
  ...SIGN_IN_HOP,
  readerId: "drop",
  flag: INCLUDE_READERS_PARAM,
};

const LIGHT_SIGN_IN_HOP: BrokerHop = {
  event: "auth.sign_in_light",
  brokerPath: "/light/google/start",
  readerId: "require",
  flag: LIGHT_SELECT_PARAM,
};

const MANAGE_HOP: BrokerHop = {
  event: "auth.manage",
  brokerPath: "/manage",
  readerId: "require",
};

type ReaderIdOutcome =
  | { readonly kind: "absent" }
  | { readonly kind: "valid"; readonly readerId: string }
  | { readonly kind: "refused"; readonly reason: "invalid" | "unproven" };

/**
 * A `readerId` counts only when this origin's own page sent it: a GET marked
 * `Sec-Fetch-Site: same-origin`, or a form POST whose `Origin` is exactly ours
 * (Safari before 16.4 sends no fetch metadata but does send `Origin` on a form
 * POST). A cross-site page or a pasted link must not choose the reader a
 * person is asked to link.
 */
function readerIdOutcome(
  request: Request,
  url: URL,
  params: URLSearchParams,
): ReaderIdOutcome {
  const raw = params.get(READER_ID_PARAM);
  if (raw === null) return { kind: "absent" };
  const proven =
    request.method === "POST"
      ? request.headers.get("origin") === url.origin
      : request.headers.get("sec-fetch-site") === "same-origin";
  if (!proven) return { kind: "refused", reason: "unproven" };
  const parsed = ReaderIdSchema.safeParse(raw);
  return parsed.success
    ? { kind: "valid", readerId: parsed.data }
    : { kind: "refused", reason: "invalid" };
}

// A query string can carry a reader id or a one-time credential.
function pathOnly(raw: string | null, base?: string): string | null {
  if (raw === null || !URL.canParse(raw, base)) return null;
  return new URL(raw, base).pathname;
}

// The Origin header is the caller's to write; only a well-formed origin
// of a sane length reaches the log.
const MAX_LOGGED_ORIGIN_LENGTH = 256;
const INVALID_ORIGIN = "invalid";

function loggableOrigin(raw: string | null): string | null {
  if (raw === null || raw === "null") return raw;
  if (raw.length > MAX_LOGGED_ORIGIN_LENGTH || !URL.canParse(raw)) {
    return INVALID_ORIGIN;
  }
  return new URL(raw).origin === raw ? raw : INVALID_ORIGIN;
}

async function readForm(request: Request): Promise<URLSearchParams | null> {
  const type = request.headers.get("content-type") ?? "";
  if (type.split(";")[0]?.trim().toLowerCase() !== FORM_CONTENT_TYPE) {
    return null;
  }
  const declared = Number(request.headers.get("content-length") ?? "0");
  if (declared > MAX_FORM_BYTES) return null;
  const body = await request.text();
  return body.length > MAX_FORM_BYTES ? null : new URLSearchParams(body);
}

/** The `/auth/*` handler bound to one app's config, cookies, guard and logger. */
export function createAuthRoutes(deps: AuthRouteDeps): {
  handleAuth: (request: Request, url: URL, env: AuthEnv) => Promise<Response>;
} {
  const { app, cookies, returnTo, log, basePath, homePath } = deps;
  const defaultOrigin = defaultOriginFor(app);

  const isAuthHost = (hostname: string, secure: boolean): boolean =>
    isAuthHostFor(app, hostname, secure);

  /**
   * The origin to send this request's user back to: its own, when we serve auth
   * on that host (the hosts are peers — sign-in finishes where it started), and
   * the default origin for any host we don't, so a `*.workers.dev` preview still
   * lands somewhere real instead of trying to hold a session it cannot keep.
   */
  const originFor = (url: URL, secure: boolean): string =>
    isAuthHost(url.hostname, secure) ? url.origin : defaultOrigin;

  /** Strip the one-time credentials from a redirect target — neither the code
   *  nor the nonce may survive into the URL bar, history, or a Referer header. */
  const cleanDest = (rawReturnTo: string | null, origin: string): URL => {
    const dest = new URL(returnTo.resolveReturnTo(rawReturnTo, origin));
    dest.searchParams.delete("code");
    dest.searchParams.delete("state");
    return dest;
  };

  /** Redirect back to the app with `?auth_error=`, incrementing the counter. */
  const failRedirect = (
    rawReturnTo: string | null,
    origin: string,
    error: "exchange_failed" | "verification_failed",
    nextTries: number,
    secure: boolean,
  ): Response => {
    const dest = cleanDest(rawReturnTo, origin);
    dest.searchParams.set("auth_error", error);
    // Only the attempt counter and the spent nonce are set here — NEVER a
    // blank session cookie, which would shadow a still-valid broker session
    // (guardrail 6). The nonce is cleared because it was matched to reach this
    // point: it is single-use whether or not the exchange behind it succeeded,
    // and the retry the user makes from the app mints a fresh one.
    return redirect(dest.toString(), [
      cookies.buildTryCookie(nextTries, secure),
      cookies.clearStateCookie(secure),
    ]);
  };

  /**
   * Refuse a callback whose `state` doesn't match this browser's stored nonce.
   *
   * Deliberately does NOT touch the circuit-breaker counter — neither
   * incrementing nor clearing it — and deliberately does NOT restart sign-in.
   * Both halves matter:
   *
   * - It cannot loop. This is a single terminal redirect to the app with an
   *   `?auth_error=`; nothing re-enters the broker, and the SPA only reads and
   *   strips the param (`consumeAuthErrorParam`), it never auto-retries.
   * - Incrementing would create the failure it is meant to prevent. A stale
   *   tab, a bookmarked callback URL, a back-button replay, or a browser
   *   prefetch — all benign, all state-less — would push the counter toward
   *   `MAX_TRIES`, and the user's NEXT genuine sign-in would then hit the
   *   circuit breaker and be told to clear their cookies.
   * - Clearing would be just as wrong: a real cookie-blocked loop must still
   *   be counted by the paths that do redirect back into a retry.
   *
   * The nonce cookie is left alone too. Clearing on mismatch would let anyone
   * who can force one bogus top-level navigation cancel a victim's in-flight
   * sign-in; leaving it costs nothing, since it is short-TTL and single-use.
   */
  const stateFailRedirect = (
    rawReturnTo: string | null,
    origin: string,
  ): Response => {
    const dest = cleanDest(rawReturnTo, origin);
    dest.searchParams.set("auth_error", "state_mismatch");
    return redirect(dest.toString());
  };

  /**
   * Circuit-breaker diagnostic page (guardrail 7). The ONE case where the
   * worker renders HTML: after `MAX_TRIES` failed round-trips another redirect
   * would just loop, so we stop and explain the likely cause (blocked cookies /
   * a stale broker cookie shadowing the session). The counter is cleared so a
   * manual retry after clearing cookies starts fresh.
   */
  const diagnosticPage = (brokerHost: string, secure: boolean): Response => {
    const body = `<!doctype html><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${app.brand} — sign-in problem</title>
<style>body{font:16px/1.6 system-ui,sans-serif;max-width:34rem;margin:18vh auto;padding:0 1.5rem;color:#1a1a1a}code{background:#f0f0f0;padding:1px 5px;border-radius:4px}a{color:#2563eb}</style>
<h1>Sign-in isn't sticking</h1>
<p>We bounced you through sign-in several times without a session taking hold.
That usually means cookies are blocked for this site, or a stale
<code>${brokerHost}</code> cookie is shadowing your session.</p>
<p>Clear cookies for <code>${brokerHost}</code> and this site (or try a private
window), then <a href="${homePath}">reload ${app.brand}</a>.</p>`;
    const headers = new Headers({ "content-type": "text/html; charset=utf-8" });
    headers.append("Set-Cookie", cookies.clearTryCookie(secure));
    return new Response(body, { status: 400, headers });
  };

  /**
   * `GET /auth/sign-in?returnTo=` (and, with `readerLinking`,
   * `/auth/sign-in-light` and `/auth/manage`) — mint the sign-in CSRF nonce,
   * store it in the state cookie, and leave (top-level) for the broker with
   * this origin's `/auth/callback` as the return target; the broker appends
   * a one-time `?code=` the callback then exchanges.
   *
   * This hop belongs to the WORKER, and that is the reason this package
   * exists. It used to belong to the page: `signIn()` minted the nonce, wrote
   * the cookie with `document.cookie`, and redirected to a broker origin the
   * bundle had decided on its own — while the worker exchanged the returned
   * code against whatever `AUTH_BROKER_ORIGIN` said and demanded that issuer.
   * On every deployed host the two agreed by coincidence of configuration; on
   * `localhost` they did not, and sign-in could not complete (see the corpus
   * history at 401ec8c). One resolver on one side of the wire is the fix.
   *
   * The nonce is the sign-in CSRF defence: it goes into a short-TTL, host-only
   * cookie AND onto the callback URL as `state`. The broker preserves the
   * callback's existing query params, so both halves come back together and
   * `/auth/callback` refuses any code whose `state` doesn't match the cookie.
   * Without it, an attacker could read a code off their own sign-in and get a
   * victim's browser to load our callback with it — `SameSite=Lax` governs
   * cookie SENDING, not SETTING, so even an `<img>` would have landed the
   * `Set-Cookie` and silently signed the victim in as the attacker.
   *
   * The cookie is `/auth`-scoped, so it never rides an asset request and each
   * sign-in overwrites rather than appends. Two tabs mid-sign-in therefore
   * share one pending nonce, and whichever finishes second gets
   * `?auth_error=state_mismatch` and signs in on the next click — a deliberate
   * trade against holding a readable multi-value nonce cookie on `/`.
   */
  const startBrokerHop = async (
    hop: BrokerHop,
    request: Request,
    url: URL,
    config: AuthConfig,
    secure: boolean,
  ): Promise<Response> => {
    const acceptsPost = hop.readerId !== "ignore";
    if (
      request.method !== "GET" &&
      !(acceptsPost && request.method === "POST")
    ) {
      return acceptsPost
        ? json(405, { error: "method_not_allowed" }, { allow: "GET, POST" })
        : methodNotAllowed();
    }
    // …and only as a top-level document navigation. This route SETS the nonce
    // cookie, so it needs the callback's gate for the same reason (see
    // `isTopLevelNavigation`): `SameSite=Lax` governs when a cookie is sent,
    // not when one may be set, so an attacker's `<img
    // src="…/auth/sign-in">` would otherwise overwrite the nonce a victim's
    // real sign-in wrote in another tab — every subsequent callback then
    // fails `checkState`, which is a repeatable sign-in denial. It would also
    // inflate `auth.sign_in`, so the gate has to precede the log line too.
    if (!isTopLevelNavigation(request)) {
      log.logAuthEvent(`${hop.event}.not_navigation`, {}, "error");
      return json(403, { error: "not_a_navigation" });
    }
    // Host gate (guardrail 2, default-deny): a host we don't serve auth on
    // cannot hold the nonce cookie, so start sign-in on one that can. A POST
    // cannot be bounced without losing its body, so it is refused.
    if (!isAuthHost(url.hostname, secure)) {
      if (request.method === "POST") {
        return json(400, { error: "unserved_host" });
      }
      return redirect(`${defaultOrigin}${url.pathname}${url.search}`);
    }

    const params =
      request.method === "POST" ? await readForm(request) : url.searchParams;
    if (params === null) {
      log.logAuthEvent(`${hop.event}.bad_form`, {}, "error");
      return json(400, { error: "bad_form" });
    }

    const reader: ReaderIdOutcome =
      hop.readerId === "ignore"
        ? { kind: "absent" }
        : readerIdOutcome(request, url, params);
    if (reader.kind === "refused" && hop.readerId === "require") {
      log.logAuthEvent(
        `${hop.event}.reader_refused`,
        { reason: reader.reason },
        "error",
      );
      return json(400, {
        error: "invalid_reader_id",
        message:
          reader.reason === "invalid"
            ? "readerId is not a reader id."
            : "readerId must come from this site's own page.",
      });
    }

    const forwarded = new URLSearchParams();
    if (reader.kind === "valid") {
      forwarded.set(READER_ID_PARAM, reader.readerId);
    }
    if (hop.flag !== undefined && params.get(hop.flag) === FLAG_ON) {
      forwarded.set(hop.flag, FLAG_ON);
    }

    const state = mintState();
    // Same guard the callback uses, reduced to a path: `returnTo` may not
    // choose the host, and the callback re-attaches it to its own origin.
    const path = returnTo.resolveReturnToPath(params.get("returnTo"));
    const callback =
      `${url.origin}${basePath}${CALLBACK_ROUTE}` +
      `?returnTo=${encodeURIComponent(path)}&state=${state}`;
    const extra = forwarded.size === 0 ? "" : `&${forwarded.toString()}`;

    log.logAuthEvent(hop.event, {});
    return redirect(
      `${config.brokerOrigin}${hop.brokerPath}` +
        `?returnTo=${encodeURIComponent(callback)}${extra}`,
      [cookies.buildStateCookie(state, secure)],
    );
  };

  const hops = new Map<string, BrokerHop>(
    app.readerLinking === true
      ? [
          [`${basePath}${SIGN_IN_ROUTE}`, READER_SIGN_IN_HOP],
          [`${basePath}${LIGHT_SIGN_IN_ROUTE}`, LIGHT_SIGN_IN_HOP],
          [`${basePath}${MANAGE_ROUTE}`, MANAGE_HOP],
        ]
      : [[`${basePath}${SIGN_IN_ROUTE}`, SIGN_IN_HOP]],
  );

  /**
   * `GET /auth/callback?code=&state=&returnTo=` — check the sign-in nonce,
   * exchange the one-time code, verify the JWT, set the session cookie, and
   * redirect to `returnTo`. Guardrail 3: every broker-dependent step is wrapped
   * (exchange/verify return values, not throws) and its specific failure is
   * logged BEFORE the redirect, so a failure is a graceful `?auth_error=` state
   * with case-distinguishing telemetry, never a blank page. The nonce check is
   * pure string work over already-total helpers, so it adds no new throw site.
   */
  const handleCallback = async (
    request: Request,
    url: URL,
    config: AuthConfig,
    secure: boolean,
  ): Promise<Response> => {
    // Only top-level GET navigations reach the broker round-trip; anything else
    // is a flat 401, keeping stray subresource requests out of the loop.
    if (request.method !== "GET") return methodNotAllowed();
    // …and only as a top-level document navigation, so a cross-site subresource
    // can never reach the `Set-Cookie` (see `isTopLevelNavigation`).
    if (!isTopLevelNavigation(request)) {
      log.logAuthEvent("auth.callback.not_navigation", {}, "error");
      return json(403, { error: "not_a_navigation" });
    }
    // Host gate (guardrail 2, default-deny). A host we serve auth on completes
    // the flow HERE — the hosts are peers, each holding its own host-only
    // cookie, so bouncing to a single canonical host would just strand the user
    // on the host they didn't start from. Anything else goes to the default.
    if (!isAuthHost(url.hostname, secure)) {
      return redirect(
        `${defaultOrigin}${basePath}${CALLBACK_ROUTE}${url.search}`,
      );
    }

    const origin = url.origin;
    const jar = cookies.parseCookies(request.headers.get("cookie"));
    const tries = cookies.readTries(jar);

    // Circuit breaker: at the cap, break the loop with a diagnostic page
    // instead of another redirect (guardrail 7).
    if (tries >= MAX_TRIES) {
      log.logAuthEvent("auth.callback.circuit_breaker", { tries }, "error");
      return diagnosticPage(new URL(config.brokerOrigin).hostname, secure);
    }

    const rawReturnTo = url.searchParams.get("returnTo");

    // Sign-in nonce (sign-in CSRF). Checked BEFORE the code is even read, so a
    // forged callback never reaches the exchange, let alone the cookie.
    const stateCheck = cookies.checkState(
      cookies.readState(jar),
      url.searchParams.get("state"),
    );
    if (stateCheck !== "ok") {
      // The verdict only — never the presented value or the stored nonce (§4).
      // A callback with no state param is a bare /start or a stale link, not
      // a forgery: only a mismatch or a missing cookie pages anyone.
      log.logAuthEvent(
        "auth.callback.state_rejected",
        { reason: stateCheck },
        stateCheck === "missing_param" ? "info" : "error",
      );
      return stateFailRedirect(rawReturnTo, origin);
    }

    const code = url.searchParams.get("code");
    if (!code) {
      log.logAuthEvent("auth.callback.missing_code", { tries }, "error");
      return failRedirect(
        rawReturnTo,
        origin,
        "exchange_failed",
        tries + 1,
        secure,
      );
    }

    // The code was minted against the host that started sign-in, so claim it
    // with that same host — gated above, so this can only be an app host.
    // Lane-gated twice, on purpose: the exchange refuses a REPORTED type the
    // app did not declare, and the verify below refuses a SIGNED `typ` it did
    // not, which is the check that cannot be skipped. `app.lanes` unset is
    // `user_session` only in both — every consumer that predates the reader
    // lane, unchanged.
    const lanes = app.lanes === undefined ? {} : { lanes: app.lanes };
    const exchanged = await exchangeCode({
      brokerOrigin: config.brokerOrigin,
      code,
      audience: exchangeAudienceFor(config, url.hostname),
      ...lanes,
    });
    if (!exchanged.ok) {
      log.logAuthEvent(
        "auth.callback.exchange_failed",
        { reason: exchanged.reason, tries },
        "error",
      );
      return failRedirect(
        rawReturnTo,
        origin,
        "exchange_failed",
        tries + 1,
        secure,
      );
    }

    const verified = await verifyBrokerJwt(
      exchanged.jwt,
      getRemoteJwks(config.brokerOrigin),
      {
        issuer: config.expectedIssuer,
        audience: config.expectedAudience,
        ...lanes,
      },
    );
    if (!verified.ok) {
      log.logAuthEvent(
        "auth.callback.verification_failed",
        { reason: verified.reason, tries },
        "error",
      );
      return failRedirect(
        rawReturnTo,
        origin,
        "verification_failed",
        tries + 1,
        secure,
      );
    }

    // Distinguished cookie-build failure case (guardrail 3). Building the
    // header is pure, but a bad `exp` is caught here rather than masked as a
    // verify miss.
    let sessionCookie: string;
    try {
      const maxAge = cookies.sessionMaxAge(verified.exp, nowSeconds());
      sessionCookie = cookies.buildSessionCookie(verified.jwt, maxAge, secure);
    } catch {
      log.logAuthEvent(
        "auth.callback.cookie_failed",
        { reason: "build" },
        "error",
      );
      return failRedirect(
        rawReturnTo,
        origin,
        "verification_failed",
        tries + 1,
        secure,
      );
    }

    log.logAuthEvent("auth.callback.success", {});
    const dest = cleanDest(rawReturnTo, origin);
    // Success clears the counter so ordinary fresh sign-ins are never
    // penalized, and burns the nonce — it is single-use.
    return redirect(dest.toString(), [
      sessionCookie,
      cookies.clearTryCookie(secure),
      cookies.clearStateCookie(secure),
    ]);
  };

  /**
   * `GET /auth/token` — serve the cookie's JWT to the SPA. Cheaply checks `exp`
   * (the downstream re-verifies the signature anyway) to avoid handing out dead
   * tokens. Absent → 401 `no_session`; expired → 401 `expired` + clear.
   */
  const handleToken = (
    request: Request,
    url: URL,
    secure: boolean,
  ): Response => {
    if (request.method !== "GET") {
      return json(401, { error: "no_session" }, { noStore: true });
    }
    // No cookie can exist on a host we don't serve auth on (guardrail 2
    // default-deny). Each peer serves its own — the cookies are host-only, so
    // a session on one is simply absent on the other.
    if (!isAuthHost(url.hostname, secure)) {
      return json(401, { error: "wrong_host" }, { noStore: true });
    }

    const token = cookies.readSession(
      cookies.parseCookies(request.headers.get("cookie")),
    );
    if (!token) {
      return json(401, { error: "no_session" }, { noStore: true });
    }

    const exp = cookies.decodeJwtExp(token);
    if (exp === null || exp <= nowSeconds()) {
      log.logAuthEvent("auth.token.expired", {});
      return json(
        401,
        { error: "expired" },
        { noStore: true, cookies: [cookies.clearSessionCookie(secure)] },
      );
    }

    return json(200, { token }, { noStore: true });
  };

  /**
   * `GET /auth/sign-out?returnTo=` — clear the session cookie, then bounce
   * through the broker sign-out so the upstream session dies too (otherwise the
   * next silent sign-in would sign the user straight back in). Returns via the
   * host sign-out was initiated on: that host's cookie is the one being
   * cleared, and sending the user to a peer would look like a silent host
   * switch, and would land them on a host still holding a session.
   *
   * GET-only, like the other two navigations. The page half sends exactly one
   * thing — `signOut()` is a `window.location.assign('/auth/sign-out?…')`, a
   * top-level GET — so a POST here is never this app's client, and admitting
   * one would only widen what a non-browser caller can do. `sec-fetch-site`
   * covers a browser; a client that omits the header is precisely what the
   * method gate stops. Sign-out is deliberately safe to repeat: it clears a
   * cookie and redirects, so nothing is lost if a prefetch or a bookmark
   * replays it.
   */
  const handleSignOut = (
    request: Request,
    url: URL,
    config: AuthConfig,
    secure: boolean,
  ): Response => {
    if (request.method !== "GET") return methodNotAllowed();
    // Cheap CSRF hardening: reject a cross-site forced sign-out.
    if (!isSameOriginRequest(request.headers, url.origin, "fetchSiteOnly")) {
      return json(403, { error: "cross_site" });
    }

    const returnPath = returnTo.resolveReturnToPath(
      url.searchParams.get("returnTo"),
    );
    const brokerSignOut = new URL(`${config.brokerOrigin}/sign-out`);
    brokerSignOut.searchParams.set(
      "returnTo",
      `${originFor(url, secure)}${returnPath}`,
    );

    log.logAuthEvent("auth.sign_out", {
      request_method: request.method,
      referer: pathOnly(request.headers.get("referer")),
      origin: loggableOrigin(request.headers.get("origin")),
      return_to: pathOnly(returnPath, url.origin),
    });
    return redirect(brokerSignOut.toString(), [
      cookies.clearSessionCookie(secure),
    ]);
  };

  /**
   * The `/auth/*` route group. `url` arrives pre-parsed from the app's entry;
   * an unmatched path under the prefix is a 404 here, not a fall-through — the
   * asset bundle has nothing meaningful at `/auth/nope`.
   *
   * The try/catch is guardrail 3, scoped deliberately to auth: every known
   * failure path already returns a value, never throws, so it should be
   * unreachable — but an auth navigation is a human mid-redirect, and the one
   * recovery that never strands them on a blank page is a redirect back to the
   * app with `?auth_error=`. An app's `/api/*` routes must NOT share this
   * catch: a keepalive beacon cannot follow a redirect, so the entry degrades
   * those to a plain 500 instead.
   */
  const handleAuth = async (
    request: Request,
    url: URL,
    env: AuthEnv,
  ): Promise<Response> => {
    const secure = url.protocol === "https:";

    // Deliberately NOT a `?auth_error=` redirect: a half-set peer pair is a
    // deploy defect no reader can retry their way out of, and the redirect
    // would hand them a toast that reads like a transient broker failure. A
    // flat 500 with a logged reason is what puts it in front of an operator.
    let config: AuthConfig;
    try {
      config = resolveConfigFor(app, env, url.hostname);
    } catch (error) {
      if (!(error instanceof AuthConfigError)) throw error;
      log.logAuthEvent("auth.misconfigured", { reason: error.reason }, "error");
      return json(500, { error: "misconfigured" });
    }

    try {
      const hop = hops.get(url.pathname);
      if (hop !== undefined) {
        return await startBrokerHop(hop, request, url, config, secure);
      }
      switch (url.pathname) {
        case `${basePath}${CALLBACK_ROUTE}`:
          return await handleCallback(request, url, config, secure);
        case `${basePath}${TOKEN_ROUTE}`:
          return handleToken(request, url, secure);
        case `${basePath}${SIGN_OUT_ROUTE}`:
          return handleSignOut(request, url, config, secure);
        default:
          return json(404, { error: "not_found" });
      }
    } catch (error) {
      log.logAuthEvent(
        "auth.unhandled",
        { reason: error instanceof Error ? error.name : "unknown" },
        "error",
      );
      return redirect(
        `${originFor(url, secure)}${homePath}?auth_error=unhandled`,
      );
    }
  };

  return { handleAuth };
}
